import React, { useEffect, useState } from "react";
import { PLAN_DATA } from "./demoConfig";
import { monthlyResults } from "../data/mock";
import { ChevronLeft, ChevronRight, Check, X, MessageCircle, TrendingUp, Clock, Target, Users, Send, Linkedin, FileText, Eye, Briefcase, ArrowRight, Zap, Globe, UserCheck, CreditCard, Smartphone, DollarSign, Monitor, Inbox, User, LayoutDashboard, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import SplitPaymentTimeline from "../components/SplitPaymentTimeline";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

const CASE_STUDIES = [
  {
    id: 1, name: "Khyati", painPoint: "Months of applying, zero callbacks", result: "Landed a role within 5 weeks", interviews: 8, tag: "No Callbacks",
    role: "Business Analyst", market: "United States", duration: "5 weeks",
    screenshotUrl: `${backendUrl}/api/static/case_studies/khyati.jpg`,
    summary: "Khyati had been applying for months with zero callbacks. After joining BulkJobApply, the team sent 780+ tailored applications targeting Business Analyst roles. Within 5 weeks, she had 8 interview calls and landed a full-time position.",
    chat: [
      { from: "client", text: "Hi, I've been applying for 3 months now and haven't received a single callback. I'm really frustrated." },
      { from: "team", text: "Hey Khyati! That's unfortunately very common. Most people apply to 10-20 jobs a week — the math just doesn't work at that volume. Let's fix that." },
      { from: "client", text: "I've probably applied to 50-60 jobs total. Is that not enough?" },
      { from: "team", text: "At a 0.4% callback rate, you'd need 250+ just to get 1 callback. We'll be sending 780+ per month, all tailored. Starting your applications today." },
      { from: "team", text: "Update: First batch of 50 applications went out today. All hand-applied on company career pages with tailored resumes." },
      { from: "client", text: "Wow that was fast! Already seeing the applications in my dashboard." },
      { from: "team", text: "Week 1 update: 195 applications sent. 2 callbacks already!" },
      { from: "client", text: "OMG I got my first interview call!! After 3 months of nothing!!" },
      { from: "team", text: "That's just the beginning. We have 585 more applications going out this month." },
      { from: "client", text: "Update — I now have 8 interview calls lined up. This is insane." },
      { from: "team", text: "Congrats Khyati! Let's make sure you nail these interviews." },
      { from: "client", text: "I GOT THE OFFER!! Thank you so much! I can't believe this worked in just 5 weeks after 3 months of nothing." },
    ]
  },
  { id: 2, name: "Ravali", screenshotUrl: `${backendUrl}/api/static/case_studies/ravali.jpg`, painPoint: "Resume kept getting rejected by ATS", result: "6 interview calls in first month", interviews: 6, tag: "ATS Rejection", role: "Software Developer", market: "India", duration: "4 weeks", summary: "Ravali's resume was getting auto-rejected by ATS systems. BulkJobApply rebuilt her resume with proper ATS formatting and keyword optimization, then sent tailored applications. She got 6 interview calls in the first month.", chat: [{ from: "client", text: "I keep applying but my applications just disappear into a black hole. I think ATS is filtering me out." }, { from: "team", text: "Ravali, you're right — 70% of resumes get rejected by ATS before a human sees them. Let me review your resume." }, { from: "team", text: "Found the issues: your resume has a two-column layout, graphics, and headers that ATS can't parse. We need to rebuild this." }, { from: "client", text: "But I paid for a fancy template! Everyone said it looked professional." }, { from: "team", text: "Looking good to humans != looking good to ATS. We'll make it both. New ATS-optimized resume with your keywords is ready." }, { from: "team", text: "Started applications today. Each one is tailored to match the job description keywords. Already sent 47 in the first 2 days." }, { from: "client", text: "I just got an email for an interview! First one in months!" }, { from: "team", text: "That's 1 of many coming. We've sent 320 applications this month so far." }, { from: "client", text: "6 interview calls now! The tailored resume approach is clearly working. Companies are actually responding!" }] },
  { id: 3, name: "Lahari", screenshotUrl: `${backendUrl}/api/static/case_studies/lahari.jpg`, painPoint: "Couldn't break into a new industry", result: "Pivoted successfully with 7 interviews", interviews: 7, tag: "Career Pivot", role: "Product Manager", market: "United States", duration: "6 weeks", summary: "Lahari was trying to pivot from engineering to product management but couldn't get past the 'no direct PM experience' barrier. BulkJobApply positioned her transferable skills and applied at volume to break through.", chat: [{ from: "client", text: "I want to move into product management but every role wants 3+ years PM experience. I only have engineering background." }, { from: "team", text: "Career pivots are tough because you're competing against people with direct experience. But your engineering background is actually a strength for PM roles." }, { from: "team", text: "We'll rewrite your resume to highlight product thinking, stakeholder management, and technical leadership. Then hit 780+ companies — some will see the value." }, { from: "client", text: "I've tried applying to maybe 30 PM roles in the last 4 months. Nothing." }, { from: "team", text: "30 isn't enough for a pivot. We need to cast a wider net. First week: 180 applications sent targeting associate PM and technical PM roles." }, { from: "client", text: "Got my first PM interview! They said they liked my technical background." }, { from: "team", text: "Exactly what we predicted. When you apply to enough companies, you find the ones that value your unique background." }, { from: "client", text: "7 interviews now, all for PM roles! Never thought a career pivot could happen this fast." }] },
  { id: 4, name: "Sahithi", screenshotUrl: `${backendUrl}/api/static/case_studies/sahithi.jpg`, painPoint: "10+ years experience but still ghosted", result: "Secured senior role interviews quickly", interviews: 9, tag: "Being Ghosted", role: "Senior Data Engineer", market: "United States", duration: "4 weeks", summary: "Despite 10+ years of experience, Sahithi was being ghosted after applications. BulkJobApply targeted senior-level positions specifically and tailored each application to show leadership and impact.", chat: [{ from: "client", text: "I have 12 years of experience as a data engineer. I shouldn't be struggling this much." }, { from: "team", text: "Senior candidates often face a paradox — overqualified for mid-level, and companies assume you'll be expensive." }, { from: "team", text: "Strategy: targeting companies actively hiring senior/lead data engineers with tailored resumes showing cost savings and team leadership." }, { from: "client", text: "3 callbacks in the first week. All senior-level roles." }, { from: "client", text: "9 interviews scheduled now. I literally have to choose which ones to prioritize!" }] },
  { id: 5, name: "Manu", screenshotUrl: `${backendUrl}/api/static/case_studies/manu.jpg`, painPoint: "Fresh graduate, no interview invites", result: "First offer within 4 weeks", interviews: 5, tag: "Fresher Struggle", role: "Frontend Developer", market: "India", duration: "4 weeks", summary: "Manu was a fresh graduate competing against thousands. BulkJobApply's volume approach got him 5 interviews and his first job offer in just 4 weeks.", chat: [{ from: "client", text: "I graduated 3 months ago and can't even get a single interview." }, { from: "team", text: "Most freshers apply to 5-10 jobs a week. We'll do 780 manual + 4,000+ via automation." }, { from: "client", text: "5 interviews this month!" }, { from: "client", text: "I GOT MY FIRST JOB OFFER! 4 weeks with BulkJobApply vs 3 months on my own." }] },
  { id: 6, name: "Soujanya", screenshotUrl: `${backendUrl}/api/static/case_studies/soujanya.jpg`, painPoint: "100+ Easy Apply, zero responses", result: "Quality applications brought 8 calls", interviews: 8, tag: "Easy Apply Trap", role: "Marketing Analyst", market: "United States", duration: "5 weeks", summary: "Soujanya was trapped in the Easy Apply cycle. BulkJobApply's human-tailored approach on career pages changed everything.", chat: [{ from: "client", text: "I've done 100+ LinkedIn Easy Apply. Not one response." }, { from: "team", text: "Easy Apply is a trap. When 500 people click 'Apply' in 2 minutes, your application is just noise." }, { from: "client", text: "8 interviews in 5 weeks. Zero from Easy Apply. 8 from your career page approach." }] },
  { id: 7, name: "Sahith & Hima", screenshotUrl: `${backendUrl}/api/static/case_studies/sahith_hima.jpg`, painPoint: "Both job hunting, double the burnout", result: "Both placed within 6 weeks", interviews: 12, tag: "Dual Search Burnout", role: "Data Analyst & QA Engineer", market: "United States", duration: "6 weeks", summary: "Sahith and Hima were both searching simultaneously. BulkJobApply handled both searches in parallel.", chat: [{ from: "client", text: "Both my spouse and I are searching. It's twice the stress." }, { from: "team", text: "We can run both searches in parallel. Two separate teams, one flat price." }, { from: "client", text: "BOTH PLACED within 6 weeks. Best investment we've ever made." }] },
  { id: 8, name: "Aman", screenshotUrl: `${backendUrl}/api/static/case_studies/aman.jpg`, painPoint: "Burned out from manual applications daily", result: "Freed up time, landed in 3 weeks", interviews: 6, tag: "Application Burnout", role: "Backend Engineer", market: "India", duration: "3 weeks", summary: "Aman was spending 5-6 hours daily on applications and burning out. BulkJobApply took over completely.", chat: [{ from: "client", text: "I'm spending 5-6 hours every day applying. Completely burned out." }, { from: "team", text: "Burnout kills quality. Let us take this off your plate." }, { from: "client", text: "GOT THE OFFER! 3 weeks. Volume + my prep time = unbeatable." }] },
  { id: 9, name: "Edil", screenshotUrl: `${backendUrl}/api/static/case_studies/edil.jpg`, painPoint: "International applicant, no local network", result: "Cracked interviews despite visa needs", interviews: 7, tag: "No Local Network", role: "Software Engineer", market: "United States", duration: "7 weeks", summary: "Edil needed H1B sponsorship with zero US network. Volume + ChillRefer helped bypass filters.", chat: [{ from: "client", text: "I need H1B sponsorship and have zero network in the US." }, { from: "team", text: "International candidates need 3-5x more applications. Volume is your only weapon." }, { from: "client", text: "7 interviews, including 3 companies willing to sponsor." }] },
  { id: 10, name: "Suvam", screenshotUrl: `${backendUrl}/api/static/case_studies/suvam.jpg`, painPoint: "Overqualified but still unemployed", result: "Found the right-fit roles quickly", interviews: 10, tag: "Overqualified", role: "Engineering Manager", market: "United States", duration: "5 weeks", summary: "Suvam had 15 years of experience but was being rejected as 'overqualified'.", chat: [{ from: "client", text: "Companies keep telling me I'm overqualified." }, { from: "team", text: "We target companies that want senior leadership." }, { from: "client", text: "10 interviews! All at the right level." }] },
  { id: 11, name: "Gopal", screenshotUrl: `${backendUrl}/api/static/case_studies/gopal.jpg`, painPoint: "Career gap making it impossible", result: "Overcame gap stigma, got interviews", interviews: 6, tag: "Career Gap", role: "Full Stack Developer", market: "India", duration: "5 weeks", summary: "Gopal had a 2-year career gap. BulkJobApply repositioned his gap and used volume.", chat: [{ from: "client", text: "I have a 2-year gap. No one will hire me." }, { from: "team", text: "We'll reframe the gap and apply at volume to find companies that don't care about gaps." }, { from: "client", text: "6 interviews and an offer! The gap didn't matter." }] },
  { id: 12, name: "Saloni", screenshotUrl: `${backendUrl}/api/static/case_studies/saloni.jpg`, painPoint: "Applying everywhere, hearing nothing back", result: "Targeted approach got results fast", interviews: 8, tag: "No Responses", role: "UX Designer", market: "United States", duration: "4 weeks", summary: "Saloni was spray-and-praying with a generic resume.", chat: [{ from: "client", text: "200+ jobs with the same resume. Not a single response." }, { from: "team", text: "We tailor for every single application. 780 of these per month." }, { from: "client", text: "8 interviews in 4 weeks. All because each application was actually tailored." }] },
  { id: 13, name: "Avi", screenshotUrl: `${backendUrl}/api/static/case_studies/avi.jpg`, painPoint: "LinkedIn not generating any leads", result: "Combined strategy brought callbacks", interviews: 5, tag: "LinkedIn Dead End", role: "Product Manager", market: "India", duration: "5 weeks", summary: "Avi relied solely on LinkedIn with zero results.", chat: [{ from: "client", text: "I've been only using LinkedIn. Zero results." }, { from: "team", text: "LinkedIn is one channel. We diversify: 780 career page apps + ChillApply + ChillRefer." }, { from: "client", text: "5 interviews! 0 from LinkedIn, 3 from career pages, 2 from ChillRefer." }] },
  { id: 14, name: "Ajith", screenshotUrl: `${backendUrl}/api/static/case_studies/avi.jpg`, painPoint: "Recruiter calls suddenly stopped coming", result: "Reignited pipeline with volume approach", interviews: 7, tag: "Dried Up Pipeline", role: "DevOps Engineer", market: "Middle East", duration: "4 weeks", summary: "Ajith's pipeline dried up. BulkJobApply targeted 780 new companies.", chat: [{ from: "client", text: "Recruiter calls completely stopped." }, { from: "team", text: "Pipelines dry up when you exhaust visible opportunities. We'll target 780 new companies." }, { from: "client", text: "7 interviews! My pipeline is overflowing again." }] },
  { id: 15, name: "Sumanth", screenshotUrl: `${backendUrl}/api/static/case_studies/sumanth.jpg`, painPoint: "6+ months searching for first interview", result: "Broke the drought in under 3 weeks", interviews: 9, tag: "Long Drought", role: "Data Scientist", market: "United States", duration: "3 weeks", summary: "Sumanth searched 6 months without a single interview. Volume broke the drought in 3 weeks.", chat: [{ from: "client", text: "6 months. Not a single interview." }, { from: "team", text: "At 10 jobs/week, that's 240 in 6 months. At 0.4% callback, barely 1. You just need more volume." }, { from: "client", text: "9 interviews in 3 weeks vs 0 in 6 months. The only difference was volume." }] },
  { id: 16, name: "Preeti", screenshotUrl: `${backendUrl}/api/static/case_studies/preeti.jpg`, painPoint: "Competing against 1000+ per role", result: "Volume + targeting beat the odds", interviews: 11, tag: "Massive Competition", role: "HR Manager", market: "India", duration: "5 weeks", summary: "Preeti faced 1000+ applicants per role. Career page focus + volume got her 11 interviews.", chat: [{ from: "client", text: "Every job I look at has 1000+ applicants." }, { from: "team", text: "Company career pages have 30-50 applicants for the same role. That's where we focus." }, { from: "client", text: "11 interviews! I went from competing against 1000 people to having recruiter attention." }] },
];

export default function DemoScreens({ state, actions }) {
  const { currentScreen } = state;
  switch (currentScreen) {
    case 1: return <ScreenWelcome state={state} actions={actions} />;
    case 2: return <ScreenQualify state={state} actions={actions} />;
    case 3: return <ScreenNumbers state={state} actions={actions} />;
    case 4: return <ScreenProof state={state} actions={actions} />;
    case 5: return <Screen3Ways state={state} actions={actions} />;
    case 6: return <ScreenSystem state={state} actions={actions} />;
    case 7: return <ScreenDay1 state={state} actions={actions} />;
    case 8: return <ScreenDashboard state={state} actions={actions} />;
    case 9: return <ScreenStack state={state} actions={actions} />;
    case 10: return <ScreenROI state={state} actions={actions} />;
    case 11: return <ScreenOffer state={state} actions={actions} />;
    case 12: return <ScreenClose state={state} actions={actions} />;
    default: return null;
  }
}

/* ══════════════════════════════════════════════════
   SCREEN 1: WELCOME
   ══════════════════════════════════════════════════ */
function ScreenWelcome({ state }) {
  const { lead } = state;
  return (
    <div className="demo-screen screen-welcome" data-testid="screen-1">
      <div className="welcome-hero">
        <div className="welcome-wave">
          <Users className="w-7 h-7" />
        </div>
        <h1 className="welcome-greeting">
          Hey {lead.clientName}!
        </h1>
        <p className="welcome-hook">
          I'm going to show you something that most job seekers<br />
          <strong>never figure out until it's too late.</strong>
        </p>
        <p className="welcome-subline">
          And then show you how we've built a system around it.
        </p>
        <div className="welcome-agenda">
          <h3 className="welcome-agenda-title">What you'll see in the next 12 minutes</h3>
          <div className="welcome-agenda-items">
            <div className="welcome-agenda-item">
              <span className="welcome-agenda-num">1</span>
              <span>Why 97% of applicants never hear back</span>
            </div>
            <div className="welcome-agenda-item">
              <span className="welcome-agenda-num">2</span>
              <span>Real results from 1500+ candidates</span>
            </div>
            <div className="welcome-agenda-item">
              <span className="welcome-agenda-num">3</span>
              <span>The 3-system approach that gets interviews</span>
            </div>
            <div className="welcome-agenda-item">
              <span className="welcome-agenda-num">4</span>
              <span>Your personalized plan to start this week</span>
            </div>
          </div>
        </div>
        <p className="welcome-footer-note">Ready? Let's dive in.</p>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 2: QUALIFY / ABOUT YOU
   ══════════════════════════════════════════════════ */
function ScreenQualify({ state }) {
  const { lead, confirmedRole, confirmedMarket, weeklyApps, linkedInConnections, linkedInReferrals, monthsSearching } = state;
  return (
    <div className="demo-screen screen-qualify" data-testid="screen-2">
      <h2 className="screen-heading-v2">Let's see where you actually stand</h2>
      <p className="screen-subheading">I'm going to show you what your current numbers actually produce — takes 60 seconds</p>
      <div className="s2-info-grid">
        <div className="s2-info-card">
          <Briefcase className="s2-info-icon" />
          <span className="s2-info-label">Target Role</span>
          <span className="s2-info-value" data-testid="qualify-role">{confirmedRole || lead.targetRole || "—"}</span>
        </div>
        <div className="s2-info-card">
          <Globe className="s2-info-icon" />
          <span className="s2-info-label">Target Market</span>
          <span className="s2-info-value" data-testid="qualify-market">{confirmedMarket || lead.targetMarket || "—"}</span>
        </div>
        <div className="s2-info-card">
          <Send className="s2-info-icon" />
          <span className="s2-info-label">Apps Per Week</span>
          <span className="s2-info-value" data-testid="qualify-apps">{weeklyApps || "—"}</span>
        </div>
        <div className="s2-info-card">
          <Linkedin className="s2-info-icon" />
          <span className="s2-info-label">LinkedIn Connections/wk</span>
          <span className="s2-info-value" data-testid="qualify-linkedin">{linkedInConnections || "—"}</span>
        </div>
        <div className="s2-info-card">
          <UserCheck className="s2-info-icon" />
          <span className="s2-info-label">Referral Requests/wk</span>
          <span className="s2-info-value" data-testid="qualify-referrals">{linkedInReferrals || "—"}</span>
        </div>
        <div className="s2-info-card">
          <Clock className="s2-info-icon" />
          <span className="s2-info-label">Months Searching</span>
          <span className="s2-info-value" data-testid="qualify-months">{monthsSearching || "—"}</span>
        </div>
      </div>
      <p className="screen-helper-text">* Your advisor is filling this in as you chat — no forms for you!</p>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 3: THE NUMBERS GAME — Solo vs Army visual
   ══════════════════════════════════════════════════ */
function ScreenNumbers({ state }) {
  const { weeklyApps, linkedInConnections, linkedInReferrals } = state;
  const apps = parseFloat(weeklyApps) || 10;
  const liConn = parseFloat(linkedInConnections) || 5;
  const liRef = parseFloat(linkedInReferrals) || 2;
  const yourApps = apps * 4;
  const yourLI = liConn * 4;
  const yourRef = liRef * 4;
  const yourCallbacks = (yourApps * 0.004).toFixed(1);
  const bjaCallbacks = 3;

  return (
    <div className="demo-screen screen-numbers" data-testid="screen-3">
      <h2 className="screen-heading-v2">Here's what your current effort actually produces</h2>
      <p className="screen-subheading">Your numbers vs. what it actually takes</p>

      <div className="solo-vs-army" data-testid="solo-vs-army">
        {/* LEFT — You, alone */}
        <div className="sva-col sva-left">
          <span className="sva-label sva-label-gray">YOU, RIGHT NOW</span>
          <div className="sva-scene sva-scene-solo">
            <svg viewBox="0 0 280 160" className="sva-svg">
              {/* Person at desk */}
              <circle cx="80" cy="45" r="14" fill="#B4B2A9" />
              <rect x="68" y="60" width="24" height="32" rx="6" fill="#B4B2A9" />
              <rect x="60" y="65" width="10" height="4" rx="2" fill="#B4B2A9" />
              <rect x="90" y="65" width="10" height="4" rx="2" fill="#B4B2A9" />
              {/* Desk + laptop */}
              <rect x="50" y="92" width="60" height="4" rx="2" fill="#D3D1C7" />
              <rect x="62" y="78" width="36" height="14" rx="3" fill="#D3D1C7" />
              <rect x="66" y="81" width="28" height="3" rx="1" fill="#E5E3DB" />
              <rect x="66" y="86" width="20" height="2" rx="1" fill="#E5E3DB" />
              {/* Sparse arrows to buildings */}
              <line x1="115" y1="70" x2="185" y2="45" stroke="#D3D1C7" strokeWidth="1.5" strokeDasharray="4,4" />
              <line x1="115" y1="80" x2="195" y2="80" stroke="#D3D1C7" strokeWidth="1.5" strokeDasharray="4,4" />
              <line x1="115" y1="90" x2="185" y2="115" stroke="#D3D1C7" strokeWidth="1.5" strokeDasharray="4,4" opacity="0.6" />
              {/* Buildings */}
              <rect x="190" y="30" width="18" height="24" rx="2" fill="#E5E3DB" /><polygon points="190,30 199,20 208,30" fill="#D3D1C7" />
              <rect x="200" y="68" width="18" height="20" rx="2" fill="#E5E3DB" /><polygon points="200,68 209,58 218,68" fill="#D3D1C7" />
              <rect x="190" y="105" width="18" height="20" rx="2" fill="#E5E3DB" /><polygon points="190,105 199,95 208,105" fill="#D3D1C7" />
            </svg>
            <span className="sva-bubble sva-bubble-gray">Applying manually<br/>one by one</span>
          </div>
          <div className="sva-stats-row">
            <div className="sva-stat-item"><span className="sva-stat-val sva-gray">{yourApps}</span><span className="sva-stat-lbl">Applications/mo</span></div>
            <div className="sva-stat-item"><span className="sva-stat-val sva-gray">{yourLI}</span><span className="sva-stat-lbl">LinkedIn/mo</span></div>
            <div className="sva-stat-item"><span className="sva-stat-val sva-gray">{yourRef}</span><span className="sva-stat-lbl">Referrals/mo</span></div>
          </div>
          <div className="sva-callback sva-callback-coral">~{Math.max(Math.round(yourApps * 0.004), 0)}-{Math.max(Math.round(yourApps * 0.015), 1)} callbacks/month</div>
        </div>

        {/* CENTER DIVIDER */}
        <div className="sva-divider"><div className="sva-divider-line" /><span className="sva-vs">VS</span></div>

        {/* RIGHT — BulkJobApply Team */}
        <div className="sva-col sva-right">
          <span className="sva-label sva-label-teal">WITH BULKJOBAPPLY</span>
          <div className="sva-scene sva-scene-army">
            <div className="sva-team-grid">
              {/* Human 1 - Application Specialist */}
              <div className="sva-figure sva-anim-1">
                <svg viewBox="0 0 56 56" className="sva-fig-svg">
                  <circle cx="28" cy="16" r="10" fill="#1D9E75" />
                  <rect x="18" y="28" width="20" height="18" rx="5" fill="#1D9E75" />
                  <rect x="12" y="32" width="8" height="3" rx="1.5" fill="#1D9E75" />
                  <rect x="36" y="32" width="8" height="3" rx="1.5" fill="#1D9E75" />
                </svg>
                <span className="sva-pill sva-pill-teal">Application specialist<br/>780 jobs/month</span>
              </div>
              {/* Human 2 - HR Specialist */}
              <div className="sva-figure sva-anim-2">
                <svg viewBox="0 0 56 56" className="sva-fig-svg">
                  <circle cx="28" cy="16" r="10" fill="#D85A30" />
                  <rect x="18" y="28" width="20" height="18" rx="5" fill="#D85A30" />
                  <rect x="22" y="36" width="12" height="8" rx="2" fill="#FAECE7" />
                </svg>
                <span className="sva-pill sva-pill-coral">HR specialist<br/>Resume + LinkedIn</span>
              </div>
              {/* AI Agent 1 - Applications */}
              <div className="sva-figure sva-anim-3 sva-ai-pulse">
                <svg viewBox="0 0 56 56" className="sva-fig-svg">
                  <rect x="8" y="8" width="40" height="30" rx="6" fill="#7F77DD" />
                  <rect x="14" y="16" width="20" height="2" rx="1" fill="#EEEDFE" />
                  <rect x="14" y="21" width="16" height="2" rx="1" fill="#EEEDFE" />
                  <rect x="14" y="26" width="22" height="2" rx="1" fill="#EEEDFE" />
                  <polygon points="38,14 42,20 34,20" fill="#EEEDFE" />
                </svg>
                <span className="sva-pill sva-pill-purple">AI agent — easy apply<br/>4,000+ jobs/month, 24/7</span>
              </div>
              {/* AI Agent 2 - Networking */}
              <div className="sva-figure sva-anim-4 sva-ai-pulse">
                <svg viewBox="0 0 56 56" className="sva-fig-svg">
                  <rect x="8" y="8" width="40" height="30" rx="6" fill="#378ADD" />
                  <circle cx="22" cy="20" r="4" fill="#E6F1FB" /><circle cx="38" cy="16" r="3" fill="#E6F1FB" /><circle cx="34" cy="28" r="3" fill="#E6F1FB" />
                  <line x1="22" y1="20" x2="38" y2="16" stroke="#E6F1FB" strokeWidth="1" /><line x1="22" y1="20" x2="34" y2="28" stroke="#E6F1FB" strokeWidth="1" />
                </svg>
                <span className="sva-pill sva-pill-blue">AI agent — networking<br/>560 touchpoints/month</span>
              </div>
            </div>
          </div>
          <div className="sva-stats-row">
            <div className="sva-stat-item"><span className="sva-stat-val sva-teal">780</span><span className="sva-stat-lbl">Manual Apps/mo</span></div>
            <div className="sva-stat-item"><span className="sva-stat-val sva-teal">4,000+</span><span className="sva-stat-lbl">Auto Apps/mo</span></div>
            <div className="sva-stat-item"><span className="sva-stat-val sva-teal">560</span><span className="sva-stat-lbl">Networking/mo</span></div>
          </div>
          <div className="sva-callback sva-callback-teal">3-12 interview callbacks/month</div>
        </div>
      </div>

      <div className="numbers-bottom-stat">
        <div className="numbers-verdict">
          <span className="numbers-verdict-label">At the industry 0.4% callback rate</span>
          <div className="numbers-verdict-row">
            <div className="numbers-verdict-you">
              <span className="nv-num">~{Math.max(Math.round((apps * 4 * 0.4) / 100), 0)}</span>
              <span className="nv-label">Your callbacks/month</span>
            </div>
            <span className="numbers-verdict-arrow">vs</span>
            <div className="numbers-verdict-bja">
              <span className="nv-num">3-12</span>
              <span className="nv-label">BulkJobApply callbacks/month</span>
            </div>
          </div>
          <span className="numbers-verdict-cta">
            Same market. Same resume. Same you. <strong>Just 20x more volume.</strong>
            <br /><span style={{fontSize: "13px", color: "#6B7280", marginTop: "4px", display: "inline-block"}}>And you put in zero effort — focus on interview prep, upskilling, or just living your life while we handle the grind.</span>
          </span>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 4: PROOF WALL — Gmail first, then stats, then case studies
   ══════════════════════════════════════════════════ */
function ScreenProof({ state, actions }) {
  const { gmailMode, selectedCaseStudies, proofTab, pdfMonth, pdfPage, pdfTotalPages, lead } = state;
  const [imageUrl, setImageUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [activeCaseStudy, setActiveCaseStudy] = useState(null);

  const currentMonthData = monthlyResults[pdfMonth];

  useEffect(() => {
    if (proofTab !== "results" || !currentMonthData?.pdfUrl) return;
    setLoadingPdf(true);
    fetch(`${backendUrl}/api/pdf-info?url=${encodeURIComponent(currentMonthData.pdfUrl)}`)
      .then((r) => r.json())
      .then((d) => { actions.setPdfTotalPages(d.page_count || 50); actions.setPdfPage(1); })
      .catch(() => actions.setPdfTotalPages(50))
      .finally(() => setLoadingPdf(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfMonth, proofTab, currentMonthData?.pdfUrl]);

  useEffect(() => {
    if (!currentMonthData?.pdfUrl) return;
    setImageUrl(`${backendUrl}/api/pdf-page-image?url=${encodeURIComponent(currentMonthData.pdfUrl)}&page=${pdfPage}&scale=1.2&quality=75`);
  }, [pdfPage, pdfMonth, currentMonthData?.pdfUrl]);

  const handleCardClick = (cs) => {
    actions.toggleCaseStudy(cs.id);
    setActiveCaseStudy(cs);
  };

  if (gmailMode) {
    return (
      <div className="demo-screen screen-proof gmail-active" data-testid="screen-4">
        <div className="gmail-reveal-overlay">
          <div className="gmail-pulse-dot" />
          <h2>Showing live client inbox...</h2>
          <p>Switch tabs now to show real interview replies</p>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-screen screen-proof" data-testid="screen-4">
      <h2 className="screen-heading-v2">Real Results, Real People</h2>

      {/* Gmail reveal — top of slide */}
      <div className="s3-gmail-prompt" data-testid="gmail-prompt">
        <Inbox className="w-5 h-5" />
        <div className="s3-gmail-prompt-text">
          <span>Show {lead.clientName} a real client's interview inbox</span>
        </div>
        <Button onClick={() => actions.setGmailMode(true)} className="s3-gmail-prompt-btn" data-testid="gmail-reveal-btn">
          Open live inbox <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="s3-proof-stats">
        <div className="s3-stat"><span className="s3-stat-val">5,00,000+</span><span className="s3-stat-lbl">Applications Sent</span></div>
        <div className="s3-stat"><span className="s3-stat-val">300+</span><span className="s3-stat-lbl">Interviews / Month</span></div>
        <div className="s3-stat"><span className="s3-stat-val">1500+</span><span className="s3-stat-lbl">Candidates Served</span></div>
        <div className="s3-stat"><span className="s3-stat-val">94%</span><span className="s3-stat-lbl">Got Interviews</span></div>
        <div className="s3-stat"><span className="s3-stat-val">18 days</span><span className="s3-stat-lbl">Avg. Time to First Interview</span></div>
      </div>

      <div className="s3-tabs">
        <button className={`s3-tab ${proofTab === "pain" ? "s3-tab-active" : ""}`} onClick={() => actions.setProofTab("pain")} data-testid="proof-tab-pain">
          Client Stories ({selectedCaseStudies.length} selected)
        </button>
        <button className={`s3-tab ${proofTab === "results" ? "s3-tab-active" : ""}`} onClick={() => actions.setProofTab("results")} data-testid="proof-tab-results">
          Monthly Results
        </button>
      </div>

      {proofTab === "pain" && (
        <div className="s3-case-grid" data-testid="case-study-grid">
          {CASE_STUDIES.map((cs) => {
            const selected = selectedCaseStudies.includes(cs.id);
            return (
              <button key={cs.id} className={`s3-case-tile ${selected ? "s3-case-selected" : ""}`} onClick={() => handleCardClick(cs)} data-testid={`case-study-${cs.id}`}>
                <div className="s3-case-top">
                  <span className="s3-case-tag">{cs.tag}</span>
                  {selected && <Check className="s3-case-check" />}
                </div>
                <span className="s3-case-name">{cs.name}</span>
                <span className="s3-case-role">{cs.role} · {cs.market}</span>
                <span className="s3-case-pain">{cs.painPoint}</span>
                {selected && (
                  <div className="s3-case-result">
                    <span className="s3-case-result-text">{cs.result}</span>
                    <span className="s3-case-interviews">{cs.interviews} interviews</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {proofTab === "results" && (
        <div className="s3-pdf-viewer" data-testid="pdf-viewer">
          <div className="s3-pdf-months">
            {monthlyResults.map((m, i) => (
              <button key={m.id} className={`s3-pdf-month-btn ${pdfMonth === i ? "s3-pdf-month-active" : ""}`} onClick={() => { actions.setPdfMonth(i); actions.setPdfPage(1); }} data-testid={`pdf-month-${m.id}`}>
                {m.month.slice(0, 3)}
              </button>
            ))}
          </div>
          <div className="s3-pdf-display">
            {loadingPdf ? <div className="s3-pdf-loading">Loading report...</div> : imageUrl ? <img src={imageUrl} alt={`${currentMonthData.month} results`} className="s3-pdf-image" /> : null}
          </div>
          <div className="s3-pdf-nav">
            <button className="s3-pdf-nav-btn" disabled={pdfPage <= 1} onClick={() => actions.setPdfPage((p) => p - 1)} data-testid="pdf-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="s3-pdf-page-info">Page {pdfPage} of {pdfTotalPages || "..."}</span>
            <button className="s3-pdf-nav-btn" disabled={pdfPage >= (pdfTotalPages || 50)} onClick={() => actions.setPdfPage((p) => p + 1)} data-testid="pdf-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {activeCaseStudy && <CaseStudyModal cs={activeCaseStudy} onClose={() => setActiveCaseStudy(null)} />}
    </div>
  );
}

/* ── CASE STUDY DETAIL MODAL ─────────────────── */
export function CaseStudyModal({ cs, onClose }) {
  const [zoomPos, setZoomPos] = useState(null);
  const imgRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose} data-testid="case-study-modal">
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cs-modal-header">
          <div className="cs-modal-header-left">
            <span className="cs-modal-tag">{cs.tag}</span>
            <h2 className="cs-modal-name">{cs.name}'s Story</h2>
            {cs.role && <span className="cs-modal-role">{cs.role} · {cs.market}</span>}
          </div>
          <button className="cs-modal-close" onClick={onClose} data-testid="close-case-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="cs-modal-stats">
          <div className="cs-modal-stat"><MessageCircle className="w-4 h-4" /><span><strong>{cs.interviews}</strong> Interviews</span></div>
          <div className="cs-modal-stat"><Clock className="w-4 h-4" /><span><strong>{cs.duration}</strong></span></div>
          <div className="cs-modal-stat cs-modal-stat-result"><TrendingUp className="w-4 h-4" /><span>{cs.result}</span></div>
        </div>
        <div className="cs-modal-summary"><Target className="w-4 h-4" /><p>{cs.summary}</p></div>
        {cs.screenshotUrl && (
          <div className="cs-modal-screenshot" data-testid="case-study-screenshot">
            <div className="cs-screenshot-label">Real WhatsApp conversation</div>
            <div
              className="cs-screenshot-zoom-container"
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setZoomPos(null)}
            >
              <img
                ref={imgRef}
                src={cs.screenshotUrl}
                alt={`${cs.name}'s real WhatsApp conversation`}
                className="cs-screenshot-img"
              />
              {zoomPos && (
                <div
                  className="cs-screenshot-magnifier"
                  style={{
                    backgroundImage: `url(${cs.screenshotUrl})`,
                    backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
                    left: `${zoomPos.x}%`,
                    top: `${zoomPos.y}%`,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 5: THE 3 PATHS — personalized
   ══════════════════════════════════════════════════ */
function Screen3Ways({ state, actions }) {
  const { revealedWays, lead, confirmedRole, confirmedMarket } = state;
  const role = confirmedRole || lead.targetRole || "your target role";
  const market = confirmedMarket || lead.targetMarket || "your market";

  const ways = [
    {
      num: "1", title: "Referrals & Networking",
      desc: "Someone inside the company refers you or a recruiter finds you through a connection. This is the highest conversion path — but most people don't network at scale.",
      stat: "Referrals have a 50% interview rate",
      personalized: `In ${market}'s market, referrals bypass the ATS entirely. This is the highest-conversion path — and almost no one works it at scale.`,
      solution: "ChillRefer",
      solutionDesc: "Sends 20 connection requests + 40 referral messages per day on LinkedIn, automatically.",
      icon: <Users className="w-6 h-6" />, color: "#7C3AED",
    },
    {
      num: "2", title: "Applications — Easy Apply + Career Pages",
      desc: "You apply directly to jobs. Easy Apply is fast but overcrowded (500+ applicants). Career page applications get 10x fewer applicants but take 10x more effort per application.",
      stat: "Career page apps convert 3x better than Easy Apply",
      personalized: `Career page applications get 10x fewer applicants than easy-apply. But they take 10x more effort per application. That's the trap most people are stuck in right now.`,
      solution: "ChillApply + Human Assistants",
      solutionDesc: "ChillApply automates easy-apply — 4,000+ jobs/month. Our human team hand-applies on career pages with tailored resumes — 780/month.",
      icon: <FileText className="w-6 h-6" />, color: "#EC4899",
    },
    {
      num: "3", title: "Recruiters Finding You",
      desc: "Recruiters search LinkedIn for candidates that match their open roles. If your profile isn't optimized with the right keywords, you're invisible to them.",
      stat: "Optimized profiles get 5x more recruiter views",
      personalized: `Recruiters in ${market} search LinkedIn by exact keywords. If your profile isn't built around ${role} terminology, you're invisible to the people actively looking to hire.`,
      solution: "LinkedIn Optimization",
      solutionDesc: "We optimize your headline, summary, skills, and keywords so recruiters find YOU — without you lifting a finger.",
      icon: <Eye className="w-6 h-6" />, color: "#0EA5E9",
    },
  ];

  return (
    <div className="demo-screen screen-3ways" data-testid="screen-5">
      <h2 className="screen-heading-v2">There are only 3 ways you're going to get a {role} interview in {market}</h2>
      <p className="screen-subheading">Every job seeker's success comes down to these three paths</p>

      <div className="ways-grid">
        {ways.map((w, i) => (
          <div key={`way-${i}`} className={`way-card ${revealedWays[i] ? "way-revealed" : ""}`} onClick={() => actions.revealWay(i)} data-testid={`way-card-${i}`}>
            <div className="way-card-header" style={{ borderColor: w.color }}>
              <div className="way-num" style={{ background: w.color }}>{w.num}</div>
              <div className="way-icon" style={{ color: w.color }}>{w.icon}</div>
              <h3>{w.title}</h3>
            </div>
            <p className="way-desc">{w.desc}</p>
            <div className="way-stat" style={{ color: w.color }}>{w.stat}</div>
            <p className="way-personalized" style={{ color: w.color }}>{w.personalized}</p>
            {revealedWays[i] && (
              <div className="way-solution" style={{ borderColor: w.color }}>
                <span className="way-solution-tag" style={{ background: w.color }}>Our Solution</span>
                <h4>{w.solution}</h4>
                <p>{w.solutionDesc}</p>
              </div>
            )}
            {!revealedWays[i] && (
              <button className="way-reveal-btn" style={{ color: w.color }}>
                Click to see our solution <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="ways-summary">
        We run all three simultaneously. <strong>You just show up to interviews.</strong>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 6: OUR SYSTEM (3-pronged attack)
   ══════════════════════════════════════════════════ */
function ScreenSystem({ state, actions }) {
  const { revealedPanels, lead } = state;
  const [activeMockup, setActiveMockup] = useState(null);
  const allRevealed = revealedPanels.every(Boolean);

  const panels = [
    {
      title: "ChillRefer", subtitle: "Automated Networking",
      features: ["20 connection requests / day to hiring managers", "40 personalized referral messages / day", "Auto-follows up with non-responders", "Targets people at companies hiring your role"],
      stat: "560 networking touchpoints / month", color: "#7C3AED", icon: <Users className="w-6 h-6" />, mockupId: "chillrefer",
    },
    {
      title: "ChillApply + Human Team", subtitle: "Applications at Scale",
      features: ["4,000+ easy-apply jobs per month (automated)", "Human assistants: 780 career page apps / month", "Each resume tailored to job description keywords", "Cover letters written for every application"],
      stat: "Up to 4,780+ applications per month", color: "#EC4899", icon: <FileText className="w-6 h-6" />, mockupId: "chillapply",
    },
    {
      title: "LinkedIn Optimization", subtitle: "Get Found by Recruiters",
      features: ["Headline rewritten with target keywords", "Summary optimized for recruiter search", "Skills section aligned to target roles", "Profile reviewed weekly for performance"],
      stat: "5x more recruiter profile views", color: "#0EA5E9", icon: <Eye className="w-6 h-6" />, mockupId: null,
    },
  ];

  return (
    <div className="demo-screen screen-system" data-testid="screen-6">
      <h2 className="screen-heading-v2">How we cover all 3 paths for you</h2>
      <p className="screen-subheading">One team, three systems, zero effort from you</p>

      <div className="system-panels">
        {panels.map((p, i) => (
          <div key={`panel-${i}`} className={`system-panel ${revealedPanels[i] ? "system-revealed" : ""}`} onClick={() => !revealedPanels[i] && actions.revealPanel(i)} data-testid={`system-panel-${i}`}>
            <div className="system-panel-header" style={{ background: revealedPanels[i] ? p.color : undefined }}>
              <span className="system-panel-icon">{p.icon}</span>
              <div><h3>{p.title}</h3><span className="system-panel-sub">{p.subtitle}</span></div>
            </div>
            {revealedPanels[i] && (
              <div className="system-panel-body">
                <ul className="system-features">
                  {p.features.map((f, fi) => (<li key={fi}><Check className="w-4 h-4" style={{ color: p.color }} />{f}</li>))}
                </ul>
                <div className="system-stat" style={{ color: p.color }}>{p.stat}</div>
                {p.mockupId && (
                  <button className="system-demo-link" style={{ background: p.color }} data-testid={`demo-toggle-${i}`} onClick={(e) => { e.stopPropagation(); setActiveMockup(activeMockup === p.mockupId ? null : p.mockupId); }}>
                    <Monitor className="w-4 h-4" /> {activeMockup === p.mockupId ? "Hide Demo" : `Show ${lead.clientName} how this works`}
                  </button>
                )}
              </div>
            )}
            {!revealedPanels[i] && (<div className="system-panel-teaser">Click to reveal</div>)}
          </div>
        ))}
      </div>

      {activeMockup === "chillrefer" && <ChillReferMockup state={state} />}
      {activeMockup === "chillapply" && <ChillApplyMockup state={state} />}

      {allRevealed && (
        <>
          <div className="system-live-demo-banner" data-testid="open-live-app">
            <span>Want to see it in action?</span>
            <Button onClick={() => window.open("https://app.bulkjobapply.com", "_blank")} className="system-live-btn">
              <ExternalLink className="w-4 h-4" /> Open Live App
            </Button>
          </div>
          <div className="system-post-reveal-banner" data-testid="post-reveal-banner">
            While you're in your next interview — <strong>all three of these are still running.</strong>
          </div>
        </>
      )}
    </div>
  );
}

/* ── CHILLREFER INLINE MOCKUP ──────────────── */
function ChillReferMockup({ state }) {
  const { lead } = state;
  const role = lead?.targetRole || "Software Engineer";
  const [sent, setSent] = useState(0);
  useEffect(() => { const t = setInterval(() => setSent((s) => s < 20 ? s + 1 : s), 600); return () => clearInterval(t); }, []);
  const profiles = [
    { name: "Meera Agarwal", title: `Hiring Manager · ${role}`, company: "Google", status: sent >= 1 ? "sent" : "pending" },
    { name: "David Chen", title: "Engineering Director", company: "Microsoft", status: sent >= 3 ? "sent" : sent >= 2 ? "sending" : "pending" },
    { name: "Priya Sharma", title: "Talent Acquisition Lead", company: "Amazon", status: sent >= 5 ? "sent" : sent >= 4 ? "sending" : "pending" },
    { name: "Alex Rivera", title: "VP Engineering", company: "Meta", status: sent >= 7 ? "sent" : sent >= 6 ? "sending" : "pending" },
    { name: "Sneha Patel", title: `Recruiter · ${role}s`, company: "Netflix", status: sent >= 9 ? "sent" : sent >= 8 ? "sending" : "pending" },
    { name: "James Liu", title: "Head of Product", company: "Stripe", status: sent >= 11 ? "sent" : sent >= 10 ? "sending" : "pending" },
    { name: "Ritu Nair", title: "CTO", company: "Notion", status: sent >= 13 ? "sent" : sent >= 12 ? "sending" : "pending" },
    { name: "Tom Wilson", title: "Senior Recruiter", company: "Spotify", status: sent >= 15 ? "sent" : sent >= 14 ? "sending" : "pending" },
  ];
  const sentCount = profiles.filter(p => p.status === "sent").length;
  return (
    <div className="inline-mockup" data-testid="chillrefer-mockup">
      <div className="mockup-browser">
        <div className="mockup-browser-bar"><div className="mockup-dots"><span className="m-dot m-dot-r" /><span className="m-dot m-dot-y" /><span className="m-dot m-dot-g" /></div><div className="mockup-url">chillrefer.com/campaigns/active</div></div>
        <div className="mockup-body mockup-chillrefer">
          <div className="cr-sidebar"><div className="cr-sidebar-title"><Users className="w-4 h-4" /><span>ChillRefer</span></div><div className="cr-sidebar-stat"><span className="cr-stat-num">{sentCount}</span><span className="cr-stat-lbl">Sent today</span></div><div className="cr-sidebar-stat"><span className="cr-stat-num">140</span><span className="cr-stat-lbl">This week</span></div><div className="cr-sidebar-stat"><span className="cr-stat-num">560</span><span className="cr-stat-lbl">This month</span></div><div className="cr-campaign-badge"><Zap className="w-3 h-3" />Campaign Active</div></div>
          <div className="cr-main"><div className="cr-main-header"><h4>Outreach Queue — {role} Hiring Managers</h4><div className="cr-progress-bar"><div className="cr-progress-fill" style={{ width: `${(sentCount / profiles.length) * 100}%` }} /></div></div><div className="cr-profiles">{profiles.map((p, i) => (<div key={i} className={`cr-profile-row cr-status-${p.status}`} data-testid={`cr-profile-${i}`}><div className="cr-avatar">{p.name.charAt(0)}</div><div className="cr-profile-info"><span className="cr-profile-name">{p.name}</span><span className="cr-profile-title">{p.title} · {p.company}</span></div><div className="cr-profile-actions">{p.status === "sent" && <span className="cr-badge cr-badge-sent"><Check className="w-3 h-3" /> Connected</span>}{p.status === "sending" && <span className="cr-badge cr-badge-sending"><Send className="w-3 h-3" /> Sending...</span>}{p.status === "pending" && <span className="cr-badge cr-badge-pending"><Clock className="w-3 h-3" /> Queued</span>}</div></div>))}</div></div>
        </div>
      </div>
      <p className="mockup-caption">Live preview — ChillRefer sends 20 connection requests + 40 referral messages per day, automatically</p>
    </div>
  );
}

/* ── CHILLAPPLY INLINE MOCKUP ──────────────── */
function ChillApplyMockup({ state }) {
  const { lead } = state;
  const role = lead?.targetRole || "Software Engineer";
  const market = lead?.targetMarket || "United States";
  const [applied, setApplied] = useState(0);
  useEffect(() => { const t = setInterval(() => setApplied((a) => a < 3000 ? a + Math.floor(Math.random() * 40) + 15 : a), 400); return () => clearInterval(t); }, []);
  const jobs = [
    { company: "Google", role: `Senior ${role}`, method: "Career Page", status: applied >= 50 ? "applied" : "applying", resume: "Tailored v3" },
    { company: "Stripe", role: role, method: "Career Page", status: applied >= 100 ? "applied" : applied >= 50 ? "applying" : "queued", resume: "Tailored v7" },
    { company: "Shopify", role: `${role} II`, method: "Easy Apply", status: applied >= 150 ? "applied" : applied >= 100 ? "applying" : "queued", resume: "Tailored v2" },
    { company: "Notion", role: role, method: "Career Page", status: applied >= 200 ? "applied" : applied >= 150 ? "applying" : "queued", resume: "Tailored v5" },
    { company: "Datadog", role: `Lead ${role}`, method: "Career Page", status: applied >= 250 ? "applied" : applied >= 200 ? "applying" : "queued", resume: "Tailored v1" },
    { company: "Figma", role: role, method: "Easy Apply", status: applied >= 300 ? "applied" : applied >= 250 ? "applying" : "queued", resume: "Tailored v4" },
    { company: "Airbnb", role: `Staff ${role}`, method: "Career Page", status: applied >= 350 ? "applied" : applied >= 300 ? "applying" : "queued", resume: "Tailored v6" },
  ];
  const appliedCount = jobs.filter(j => j.status === "applied").length;
  return (
    <div className="inline-mockup" data-testid="chillapply-mockup">
      <div className="mockup-browser">
        <div className="mockup-browser-bar"><div className="mockup-dots"><span className="m-dot m-dot-r" /><span className="m-dot m-dot-y" /><span className="m-dot m-dot-g" /></div><div className="mockup-url">chillapply.com/dashboard</div></div>
        <div className="mockup-body mockup-chillapply">
          <div className="ca-top-bar"><div className="ca-stat-row"><div className="ca-stat-box"><span className="ca-stat-num">{Math.min(applied, 4000).toLocaleString()}</span><span className="ca-stat-lbl">Easy Apply Sent</span></div><div className="ca-stat-box"><span className="ca-stat-num">4,000+</span><span className="ca-stat-lbl">Monthly Target</span></div><div className="ca-stat-box"><span className="ca-stat-num">{appliedCount}</span><span className="ca-stat-lbl">Today's Batch</span></div><div className="ca-stat-box ca-stat-highlight"><span className="ca-stat-num">{market}</span><span className="ca-stat-lbl">Target Market</span></div></div><div className="ca-progress"><div className="ca-progress-label"><span>Monthly Progress</span><span>{Math.min(Math.round((applied / 4000) * 100), 100)}%</span></div><div className="ca-progress-track"><div className="ca-progress-fill" style={{ width: `${Math.min((applied / 4000) * 100, 100)}%` }} /></div></div></div>
          <div className="ca-table"><div className="ca-table-header"><span className="ca-th">Company</span><span className="ca-th">Role</span><span className="ca-th">Method</span><span className="ca-th">Resume</span><span className="ca-th">Status</span></div>{jobs.map((j, i) => (<div key={i} className={`ca-table-row ca-row-${j.status}`} data-testid={`ca-job-${i}`}><span className="ca-td ca-td-company"><span className="ca-company-dot" />{j.company}</span><span className="ca-td">{j.role}</span><span className="ca-td"><span className={`ca-method ${j.method === "Easy Apply" ? "ca-method-easy" : "ca-method-career"}`}>{j.method}</span></span><span className="ca-td ca-td-resume">{j.resume}</span><span className="ca-td">{j.status === "applied" && <span className="ca-badge ca-badge-applied"><Check className="w-3 h-3" /> Applied</span>}{j.status === "applying" && <span className="ca-badge ca-badge-applying"><Send className="w-3 h-3" /> Applying...</span>}{j.status === "queued" && <span className="ca-badge ca-badge-queued"><Clock className="w-3 h-3" /> Queued</span>}</span></div>))}</div>
        </div>
      </div>
      <p className="mockup-caption">Live preview — 780 human-applied career page applications + 4,000+ automated Easy Apply, every month</p>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 7: YOUR FIRST WEEK — second-person, personalized
   ══════════════════════════════════════════════════ */

// Compute a calendar date that is `dayOffset` days after the lead's anchor day.
//
// Anchor priority:
//   1. lead.scheduledAt          — booked demo time (most accurate)
//   2. state.demoStartedAt       — when the closer hit "Start demo" (walk-in fallback;
//                                  prevents Day 0 from drifting if the call crosses midnight)
//   3. lead.demoDoneAt           — only set after the demo ends (rare during render)
//   4. new Date()                — last-resort: today
//
// Timezone priority:
//   1. lead.scheduledTz          — the lead's booked timezone (e.g. "Asia/Kolkata")
//   2. browser local timezone    — derived from Intl.DateTimeFormat().resolvedOptions().timeZone
//                                  so a US closer running an unscheduled demo gets US dates,
//                                  not IST.
function _anchorDateInTZ(lead, demoStartedAt) {
  let tz = lead?.scheduledTz;
  if (!tz) {
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { tz = "UTC"; }
  }
  const iso = lead?.scheduledAt || demoStartedAt || lead?.demoDoneAt || new Date().toISOString();
  let base;
  try {
    base = new Date(iso);
    if (Number.isNaN(base.getTime())) base = new Date();
  } catch { base = new Date(); }
  // Get the calendar Y-M-D in the target timezone
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(base);
  const y = parts.find(p => p.type === "year")?.value;
  const m = parts.find(p => p.type === "month")?.value;
  const d = parts.find(p => p.type === "day")?.value;
  // Anchor at noon UTC to avoid DST edge cases when we add days
  return new Date(`${y}-${m}-${d}T12:00:00Z`);
}

function _addDays(date, n) {
  const d = new Date(date.getTime());
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}

function _formatDay(date) {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short", month: "short", day: "numeric",
  }).format(date); // e.g. "Mon, Apr 28"
}

function _formatRange(start, end) {
  // Always render end with the same "Weekday, Mon Day" format for clean reading.
  // E.g. "Wed, Apr 29 – Thu, Apr 30"
  const fmt = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function ScreenDay1({ state }) {
  const { lead, confirmedRole, confirmedMarket, demoStartedAt } = state;
  const role = confirmedRole || lead.targetRole || "your target role";
  const market = confirmedMarket || lead.targetMarket || "your market";

  // Anchor "Day 0" to the lead's actual demo time (in their timezone).
  const anchor = _anchorDateInTZ(lead, demoStartedAt);
  const day = (n) => _formatDay(_addDays(anchor, n));
  const dayRange = (a, b) => _formatRange(_addDays(anchor, a), _addDays(anchor, b));

  const steps = [
    { day: "Day 0", date: day(0), title: "Your WhatsApp Group Goes Live", desc: `Your WhatsApp group goes live — your assistant, your manager, your HR specialist, all in one thread. This is your direct line to the team working on your job search.`, icon: <MessageCircle className="w-5 h-5" />, color: "#25D366", highlight: "Your team of 3 — your manager, your HR specialist, your dedicated assistant" },
    { day: "Day 0", date: day(0), title: "Onboarding Form", desc: `You'll receive a quick onboarding form to capture everything needed: target roles, preferred companies, locations, salary expectations, visa status, and any special requirements. This ensures every application is tailored to your exact goals.`, icon: <FileText className="w-5 h-5" />, color: "#7C3AED", highlight: "10-minute form — then your team handles the rest" },
    { day: "Day 1", date: day(1), title: "Target Company List Sent to You", desc: `Your assistant sends you a preview of the first 10 companies being targeted for ${role} roles in ${market}. You approve, adjust, or add to the list.`, icon: <Target className="w-5 h-5" />, color: "#D85A30", highlight: "You control the target list — nothing goes out without your approval" },
    { day: "Day 1", date: day(1), title: "Gmail + LinkedIn Setup", desc: `You'll create a fresh Gmail account and connect it to your existing LinkedIn. This allows your team to use Google login for company career pages and manage applications seamlessly — your personal accounts stay untouched.`, icon: <Globe className="w-5 h-5" />, color: "#0EA5E9", highlight: "Secure access — your personal accounts stay untouched" },
    { day: "Day 1–2", date: dayRange(1, 2), title: "Your ATS Resume Rebuild", desc: `Your HR specialist — assigned to you on Day 0 — rebuilds your resume from scratch for ${role} roles in ${market}. ATS-optimized formatting, keyword-rich content, quantified achievements. This becomes your base resume, tailored for every single job description automatically.`, icon: <FileText className="w-5 h-5" />, color: "#EC4899", highlight: "Your base resume + auto-tailored per job description" },
    { day: "Day 2", date: day(2), title: "You Review & Approve", desc: "Your rebuilt resume is sent to you for review. Once you approve it, your application engine fires up. Nothing goes out without your green light.", icon: <Check className="w-5 h-5" />, color: "#F59E0B", highlight: "Full control — nothing happens without your approval" },
    { day: "Day 3", date: day(3), title: "Your Applications Begin", desc: "Your dedicated assistant starts applying on company career pages with your tailored resumes. ChillApply runs easy-apply in parallel. ChillRefer starts sending connection requests and referral messages. All three of your engines running simultaneously.", icon: <Zap className="w-5 h-5" />, color: "#1D9E75", highlight: "3 systems activated — your applications, your networking, your profile optimization" },
    { day: "Day 3+", date: `${day(3)} onwards`, title: "Track Everything on Your Dashboard", desc: "You get access to your BulkJobApply dashboard where you can see every application in real time — which company, job link, role applied, resume used, application status. Complete transparency, zero guesswork.", icon: <Eye className="w-5 h-5" />, color: "#6366F1", highlight: "Your real-time tracking — every job, every resume, every status" },
  ];

  // Sub-line: tell the lead which timezone the dates are anchored to,
  // and warn if dates are anchored to "today" because no demo was scheduled.
  let tzLabel = "";
  let anchorNote = "";
  const map = { "Asia/Kolkata": "IST", "America/New_York": "EST", "America/Los_Angeles": "PST", "Europe/London": "GMT", "Asia/Dubai": "GST", "Australia/Sydney": "AEST" };
  if (lead?.scheduledTz) {
    tzLabel = map[lead.scheduledTz] || lead.scheduledTz;
  } else {
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      tzLabel = map[browserTz] || browserTz;
    } catch { /* ignore */ }
  }
  if (!lead?.scheduledAt) {
    anchorNote = "(anchored to today — no scheduled demo time on file)";
  }

  return (
    <div className="demo-screen screen-day1" data-testid="screen-7">
      <h2 className="screen-heading-v2">Here's exactly what your first week looks like, {lead.clientName}</h2>
      <p className="screen-subheading">
        From your demo today to first applications in just 3 days
        {tzLabel && <span className="day1-tz-note"> · all dates in {tzLabel}</span>}
        {/* Closer-only sentinel — invisible to the lead but exposed for tests + closer sidebar */}
        {anchorNote && <span className="day1-anchor-note-hidden" data-testid="day1-anchor-note">{anchorNote}</span>}
      </p>
      <div className="day1-timeline-v2">
        {steps.map((s, i) => (
          <div key={`step-${i}`} className="day1-step-v2" data-testid={`day1-step-${i}`}>
            <div className="day1-marker-v2">
              <div className="day1-icon-circle" style={{ background: s.color }}>{s.icon}</div>
              {i < steps.length - 1 && <div className="day1-connector" />}
            </div>
            <div className="day1-card">
              <div className="day1-card-top">
                <div className="day1-day-stack">
                  <span className="day1-day-badge" style={{ background: s.color }}>{s.day}</span>
                  <span className="day1-date-line" data-testid={`day1-date-${i}`}>{s.date}</span>
                </div>
                <h3 className="day1-card-title">{s.title}</h3>
              </div>
              <p className="day1-card-desc">{s.desc}</p>
              <div className="day1-card-highlight" style={{ borderColor: s.color, color: s.color }}>{s.highlight}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 8: YOUR DASHBOARD
   ══════════════════════════════════════════════════ */
function ScreenDashboard({ state }) {
  const { lead } = state;
  const [activeSpot, setActiveSpot] = useState(null);
  const dashboardUrl = "https://customer-assets.emergentagent.com/job_13a9841d-abee-4393-b69d-dc58934d1b0a/artifacts/xi9u2sem_image.png";
  const hotspots = [
    { id: "stats", top: "22%", left: "35%", label: "Live Stats", desc: "Profile completion, total applications, delegated vs manual jobs — all in real time" },
    { id: "jobs", top: "58%", left: "40%", label: "Recent Jobs", desc: "Every application with company name, role, job link, date — fully transparent" },
    { id: "team", top: "48%", left: "82%", label: "Your Team", desc: "Your dedicated human assistant with their profile and success metrics" },
    { id: "nav", top: "30%", left: "10%", label: "Full Portal", desc: "Dashboard, My Jobs, Profile — everything organized in one clean portal" },
  ];
  const features = [
    { icon: <Eye className="w-4 h-4" />, title: "Real-time Tracking", desc: "See every application as it happens" },
    { icon: <FileText className="w-4 h-4" />, title: "Job Details", desc: "Company, role, job link, resume used" },
    { icon: <Users className="w-4 h-4" />, title: "Assigned Team", desc: "Your dedicated human assistant" },
    { icon: <TrendingUp className="w-4 h-4" />, title: "Progress Stats", desc: "Total applications, success rate" },
  ];
  return (
    <div className="demo-screen screen-dashboard" data-testid="screen-8">
      <h2 className="screen-heading-v2">This is what you'll check every morning, {lead.clientName}</h2>
      <p className="screen-subheading">Every application. Every company. Every status. All in one place — you never wonder what's happening.</p>
      <div className="dash-showcase">
        <div className="dash-browser-frame">
          <div className="dash-browser-bar"><div className="dash-browser-dots"><span className="dash-dot dash-dot-red" /><span className="dash-dot dash-dot-yellow" /><span className="dash-dot dash-dot-green" /></div><div className="dash-browser-url"><span>app.bulkjobapply.com/dashboard/client</span></div></div>
          <div className="dash-img-container">
            <img src={dashboardUrl} alt="BulkJobApply Client Dashboard" className="dash-screenshot" data-testid="dashboard-screenshot" />
            {hotspots.map((h) => (
              <button key={h.id} className={`dash-hotspot ${activeSpot === h.id ? "dash-hotspot-active" : ""}`} style={{ top: h.top, left: h.left }} onClick={() => setActiveSpot(activeSpot === h.id ? null : h.id)} data-testid={`hotspot-${h.id}`}>
                <span className="dash-hotspot-pulse" /><span className="dash-hotspot-dot" />
                {activeSpot === h.id && (<div className="dash-hotspot-tooltip"><strong>{h.label}</strong><p>{h.desc}</p></div>)}
              </button>
            ))}
          </div>
        </div>
        <p className="dash-real-caption" data-testid="dash-caption">Real client dashboard — Sreekar R., Software Engineer, 404 applications in Month 1.</p>
        <div className="dash-features">
          {features.map((f, i) => (<div key={i} className="dash-feature-card" data-testid={`dash-feature-${i}`}><div className="dash-feature-icon">{f.icon}</div><div><h4>{f.title}</h4><p>{f.desc}</p></div></div>))}
        </div>
      </div>
      <div className="dash-callout">No more guessing. No more "did they apply?" anxiety. Every application tracked, every resume stored, every status updated — all in one place.</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 9: THE STACK (everything you get — revealed one at a time)
   ══════════════════════════════════════════════════ */
function ScreenStack({ state }) {
  const { lead, confirmedRole, confirmedMarket, revealedStackItems } = state;
  const role = confirmedRole || lead.targetRole || "your target role";
  const market = confirmedMarket || lead.targetMarket || "your market";

  const stackItems = [
    { icon: <User className="w-5 h-5" />, title: "Dedicated human assistant", body: `Applies to 780+ jobs per month on company career pages with your tailored resume and cover letter. Not automated. A real person, every application.`, color: "#1D9E75", bigNum: "780", unit: "jobs/month", badge: "tailored resume, every application" },
    { icon: <Zap className="w-5 h-5" />, title: "ChillApply — easy-apply automation", body: `4,000+ easy-apply jobs automated every month across all major job boards. Runs 24/7. Zero effort from you.`, color: "#7F77DD", bigNum: "4,000+", unit: "easy-apply/month", badge: "runs 24/7, zero effort" },
    { icon: <Users className="w-5 h-5" />, title: "ChillRefer — LinkedIn networking", body: `560 networking touchpoints per month. Connection requests and personalized messages to hiring managers at companies actively hiring for ${role} roles in ${market}.`, color: "#378ADD", bigNum: "560", unit: "touchpoints/month", badge: "hiring managers at your target companies" },
    { icon: <FileText className="w-5 h-5" />, title: "ATS-optimized resume — rebuilt for your role", body: `Your HR specialist rebuilds your resume from scratch, optimized for ${role} keyword patterns and ATS systems used by companies in ${market}.`, color: "#D85A30", bigNum: "100%", unit: "rebuilt for your role", badge: `keyword-matched to ${role}` },
    { icon: <Linkedin className="w-5 h-5" />, title: "LinkedIn profile optimization", body: `Headline, summary, and skills section rewritten so recruiters searching for ${role} candidates in ${market} find you — not your competition.`, color: "#0077B5", bigNum: "5x", unit: "more recruiter views", badge: `optimized for ${market} search` },
    { icon: <MessageCircle className="w-5 h-5" />, title: "Direct WhatsApp access to your team", body: `Your assistant, manager, and HR specialist — all in one WhatsApp group. Message them directly. Real people, real responses.`, color: "#25D366", bigNum: "4", unit: "people in your corner", badge: "assistant + manager + HR + you" },
    { icon: <LayoutDashboard className="w-5 h-5" />, title: "Real-time application dashboard", body: `See every application as it goes out. Company name, role, date, status — full visibility. Nothing is a black box.`, color: "#888780", bigNum: "0", unit: "black boxes", badge: "every application visible in real time" },
  ];

  return (
    <div className="demo-screen screen-stack" data-testid="screen-9">
      <h2 className="screen-heading-v2">Here's everything working for you, {lead.clientName}</h2>
      <p className="screen-subheading">All of this runs simultaneously — from Day 3</p>

      <div className="stack-items">
        {stackItems.map((item, i) => (
          <div key={`stack-${i}`} className={`stack-item ${i < revealedStackItems ? "stack-item-visible" : "stack-item-hidden"}`} style={{ borderLeftColor: i < revealedStackItems ? item.color : "transparent" }} data-testid={`stack-item-${i}`}>
            <div className="stack-item-icon" style={{ background: item.color }}>{item.icon}</div>
            <div className="stack-item-content">
              <div className="stack-num-row">
                <span className="stack-big-num" style={{ color: item.color }}>{item.bigNum}</span>
                <span className="stack-unit">{item.unit}</span>
              </div>
              <h4 className="stack-item-title">{item.title}</h4>
              <p className="stack-item-body">{item.body}</p>
              <span className="stack-badge" style={{ background: `${item.color}18`, color: item.color }}>{item.badge}</span>
            </div>
          </div>
        ))}
      </div>

      {revealedStackItems >= 7 && (
        <div className="stack-closing-question" data-testid="stack-closing-question">
          What would you pay if you hired each of these separately?
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 10: ROI CALCULATOR (moved before pricing)
   ══════════════════════════════════════════════════ */
function ScreenROI({ state, actions }) {
  const { salaryInput, monthsInput, selectedPlan, recommendedPlan, lead } = state;
  const salary = parseFloat(salaryInput) || 0;
  const months = parseFloat(monthsInput) || 0;
  // Always default to a real plan — never tools-access (removed)
  const planKey = selectedPlan || recommendedPlan || "1-month-full-stack";
  const plan = PLAN_DATA[planKey] || PLAN_DATA["1-month-full-stack"];
  const planPrice = plan ? parseFloat(plan.price.replace(/[^0-9.]/g, "")) : 399;
  const isGuaranteed = planKey === "guaranteed";

  // DIY scenario: 6 months to land a job doing it manually
  const diyMonths = Math.max(months, 3) + 3; // they've already spent X months, likely 3+ more
  const diyTotalLost = salary * diyMonths;
  const diyEffortHours = diyMonths * 4 * 20; // ~20 hours/week for months

  // With BulkJobApply: land in ~1-2 months
  const bjaMonths = 1;
  const bjaTotalCost = planPrice;
  const bjaFirstSalary = salary * 1; // first month's salary earned
  const netGainWithBja = bjaFirstSalary - bjaTotalCost;
  const moneySaved = diyTotalLost - (salary * bjaMonths + bjaTotalCost);

  return (
    <div className="demo-screen screen-roi" data-testid="screen-10">
      <h2 className="screen-heading-v2">Let's talk about the real ROI, {lead.clientName}</h2>
      <p className="screen-subheading">This isn't a cost — it's the fastest shortcut to your first paycheck</p>
      <div className="s6-input-row">
        <div className="s6-input-group"><label>Expected Monthly Salary</label><div className="s6-input-wrap"><DollarSign className="w-4 h-4" /><input type="number" value={salaryInput} onChange={(e) => actions.setSalaryInput(e.target.value)} placeholder="e.g. 6000" className="s6-input" data-testid="roi-salary" /></div></div>
        <div className="s6-input-group"><label>Months Searching So Far</label><div className="s6-input-wrap"><Clock className="w-4 h-4" /><input type="number" value={monthsInput} onChange={(e) => actions.setMonthsInput(e.target.value)} placeholder="e.g. 3" className="s6-input" data-testid="roi-months" /></div></div>
      </div>
      {salary > 0 && (
        <>
          <div className="roi-comparison" data-testid="roi-results">
            <div className="roi-path roi-path-diy">
              <span className="roi-path-label">Continue on your own</span>
              <div className="roi-path-num">${diyTotalLost.toLocaleString()}</div>
              <div className="roi-path-breakdown">
                <div className="roi-line"><span>Salary lost ({diyMonths} more months)</span><span className="roi-line-val roi-red">${diyTotalLost.toLocaleString()}</span></div>
                <div className="roi-line"><span>Hours spent applying</span><span className="roi-line-val roi-red">{diyEffortHours}+ hrs</span></div>
                <div className="roi-line"><span>Mental toll & burnout</span><span className="roi-line-val roi-red">Priceless</span></div>
              </div>
              <div className="roi-path-total">
                <span>Net outcome</span>
                <span className="roi-red">-${diyTotalLost.toLocaleString()} lost + months of stress</span>
              </div>
            </div>

            <div className="roi-vs-circle">VS</div>

            <div className="roi-path roi-path-bja">
              <span className="roi-path-label">With BulkJobApply</span>
              <div className="roi-path-num roi-teal">${salary.toLocaleString()}/mo</div>
              <div className="roi-path-breakdown">
                <div className="roi-line">
                  <span>{plan?.name || "Plan"} investment</span>
                  <span className="roi-line-val">{isGuaranteed ? "$750 + $750" : `$${planPrice.toLocaleString()}`}</span>
                </div>
                <div className="roi-line"><span>First salary (Month 1)</span><span className="roi-line-val roi-green">+${salary.toLocaleString()}</span></div>
                <div className="roi-line"><span>Your effort</span><span className="roi-line-val roi-green">Show up to interviews</span></div>
              </div>
              <div className="roi-path-total roi-path-total-green">
                <span>Net outcome</span>
                <span className="roi-green">+${netGainWithBja.toLocaleString()} in Month 1 alone</span>
              </div>
            </div>
          </div>

          <div className="roi-summary-banner">
            <strong>The math is simple:</strong>{" "}
            {isGuaranteed
              ? <>You invest <strong>$750 today — and $750 only after your first interview</strong>. You start earning ${salary.toLocaleString()}/month within weeks. </>
              : <>You invest ${planPrice.toLocaleString()} now and start earning ${salary.toLocaleString()}/month within weeks. </>
            }
            Or you spend {diyMonths}+ more months doing it alone, losing ${salary.toLocaleString()} every single month.
            {moneySaved > 0 && <><br/><strong>That's ${moneySaved.toLocaleString()} you keep in your pocket by starting today.</strong></>}
          </div>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 11: OFFER / PRICING (Guaranteed first, then reveal all)
   ══════════════════════════════════════════════════ */
function ScreenOffer({ state, actions }) {
  const { recommendedPlan, selectedPlan, lead, monthsInput, monthsSearching, confirmedRole, confirmedMarket } = state;
  const plans = Object.entries(PLAN_DATA);
  const role = confirmedRole || lead.targetRole || "your target role";
  const market = confirmedMarket || lead.targetMarket || "your market";
  const months = monthsInput || monthsSearching || "—";
  const recKey = recommendedPlan;
  const [showAiRec, setShowAiRec] = useState(false);

  const PLAN_REASONS = {
    "1-month-full-stack": "You're early in your search — 1 month gets you immediate traction and a massive head start. Most clients see callbacks in weeks 3–4.",
    "2-month-full-stack": "This is the plan most clients land on. Month 1 builds the pipeline. Month 2 is where interviews happen. Weeks 5–8 is when most offers come.",
    "guaranteed": "You've been searching for a while. $750 today, $750 after your first interview — we don't stop until you have 10 interviews. No risk.",
  };

  const VALUE_BARS = { "1-month-full-stack": 55, "2-month-full-stack": 100, "guaranteed": 85 };

  return (
    <div className="demo-screen screen-offer" data-testid="screen-11">
      <h2 className="screen-heading-v2">Choose the plan that fits you, {lead.clientName}</h2>
      <p className="screen-subheading">Every plan includes our full system — pick the duration that works for your search</p>

      <div className="s7-plans-grid" data-testid="offer-plans">
        {plans.map(([key, plan]) => {
          const isSel = key === selectedPlan;
          const isMostPopular = key === "2-month-full-stack";
          const isGuaranteed = key === "guaranteed";
          const isHighlighted = showAiRec && key === recKey;
          return (
            <div key={key} className={`s7-plan-card ${isMostPopular ? "s7-best-value" : ""} ${isGuaranteed ? "s7-premium" : ""} ${isSel ? "s7-selected" : ""} ${isHighlighted ? "s7-ai-highlighted" : ""}`} data-testid={`plan-card-${key}`}>
              {isMostPopular && <span className="s7-best-value-badge">Most Popular</span>}
              {isGuaranteed && <span className="s7-premium-badge">We Work Till Results</span>}
              {isHighlighted && <span className="s7-ai-rec-badge">AI Recommended</span>}
              <h3 className="s7-plan-name">{plan.name}</h3>
              <span className="s7-plan-price">{plan.price}</span>
              {isMostPopular && <span className="s7-savings-badge">Most clients land their offer in weeks 5–8.</span>}
              {isGuaranteed && <SplitPaymentTimeline variant="dark" compact />}
              <ul className="s7-plan-features">
                {plan.features.map((f, i) => (<li key={`feat-${i}`}><Check className="w-4 h-4" />{f}</li>))}
              </ul>
              <div className="s7-value-bar-wrap">
                <span className="s7-value-bar-label">Value per dollar</span>
                <div className="s7-value-bar-track"><div className="s7-value-bar-fill" style={{ width: `${VALUE_BARS[key] || 50}%`, background: isMostPopular ? "#1D9E75" : isHighlighted ? "#1D9E75" : isGuaranteed ? "#F59E0B" : "#B4B2A9" }} /></div>
              </div>
              <Button className={`s7-select-btn ${isSel ? "s7-select-active" : ""} ${isGuaranteed ? "s7-select-premium" : ""}`} onClick={() => actions.setSelectedPlan(isSel ? null : key)} data-testid={`select-plan-${key}`}>
                {isSel ? "Selected" : "Select This Plan"}
              </Button>
            </div>
          );
        })}
      </div>

      {showAiRec && recKey && (
        <div className="offer-ai-reason" data-testid="ai-recommendation">
          <strong>Why {PLAN_DATA[recKey]?.name}?</strong> {PLAN_REASONS[recKey]}
        </div>
      )}

      <button className="offer-ai-btn" onClick={() => setShowAiRec(!showAiRec)} data-testid="ai-recommend-btn">
        {showAiRec ? "Hide recommendation" : "Which plan fits me best?"}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SCREEN 12: CLOSE & PAYMENT
   ══════════════════════════════════════════════════ */
function ScreenClose({ state, actions }) {
  const { lead, selectedPlan, recommendedPlan, paymentMethod, settings, auth, confirmedMarket } = state;
  const planKey = selectedPlan || recommendedPlan;
  const plan = PLAN_DATA[planKey];
  const isGuaranteed = planKey === "guaranteed";
  const market = (confirmedMarket || lead.targetMarket || "").toLowerCase();
  const paymentTag = market.includes("india") ? "upi" : market.includes("germany") || market.includes("middle east") ? "remitly" : "paypal";

  const paymentOptions = [
    { id: "paypal", label: "PayPal", icon: <CreditCard className="w-5 h-5" />, desc: "International payments", borderColor: "#003087" },
    { id: "upi", label: "UPI", icon: <Smartphone className="w-5 h-5" />, desc: "India — instant transfer", borderColor: "#097939" },
    { id: "remitly", label: "Remitly / Wire", icon: <Globe className="w-5 h-5" />, desc: "Bank transfer", borderColor: "#6B46C1" },
  ];

  return (
    <div className="demo-screen screen-close-v2" data-testid="screen-12">
      <div className="close-question-box" data-testid="close-question">
        <p className="close-question-main">{lead.clientName}, does this make sense for your situation?</p>
      </div>

      <div className="close-hero-box">
        <Zap className="close-hero-icon" />
        <h2 className="close-hero-title">Let's get you started, {lead.clientName}</h2>
        {plan && (
          <div className="close-plan-summary">
            <span className="close-plan-name">{plan.name}</span>
            <span className="close-plan-price">{plan.price}</span>
          </div>
        )}
        {isGuaranteed && (
          <div className="close-plan-split" data-testid="close-split-payment" style={{ marginTop: "8px", color: "#1D9E75", fontWeight: 600, fontSize: "14px" }}>
            $750 today — $750 after your first interview
          </div>
        )}
        {isGuaranteed && (
          <div style={{ marginTop: "12px" }}>
            <SplitPaymentTimeline variant="light" />
          </div>
        )}
      </div>

      {/* Trust badges */}
      <div className="close-trust-row" data-testid="trust-badges">
        <div className="close-trust-item"><Check className="w-4 h-4" /><span>Secure payment</span></div>
        <div className="close-trust-item"><Target className="w-4 h-4" /><span>30-day support</span></div>
        <div className="close-trust-item"><Zap className="w-4 h-4" /><span>Starts within 24h</span></div>
      </div>

      <div className="close-payment-section">
        <h3 className="close-payment-title">How would you like to pay?</h3>
        <div className="close-payment-options">
          {paymentOptions.map((opt) => (
            <button key={opt.id} className={`close-payment-btn ${paymentMethod === opt.id ? "close-payment-active" : ""}`} style={{ borderLeftColor: opt.borderColor, borderLeftWidth: "4px", borderLeftStyle: "solid" }} onClick={() => actions.setPaymentMethod(opt.id)} data-testid={`payment-${opt.id}`}>
              {opt.icon}
              <div>
                <span className="close-payment-name">{opt.label}</span>
                <span className="close-payment-desc">{opt.desc}</span>
                {opt.id === paymentTag && <span className="close-payment-tag">Most used by {lead.targetMarket} clients</span>}
              </div>
            </button>
          ))}
        </div>
        {paymentMethod && (
          <div className="close-payment-details" data-testid="payment-details">
            <p className="close-payment-instruction">
              {isGuaranteed
                ? "I'll send you the payment link for $750 on WhatsApp right now. The second $750 is only due after we book your first interview."
                : <>
                    {paymentMethod === "paypal" && "I'll send you the PayPal payment link right now on WhatsApp."}
                    {paymentMethod === "upi" && "I'll share our UPI ID on WhatsApp — you can pay instantly from any UPI app."}
                    {paymentMethod === "remitly" && "I'll send you our bank details on WhatsApp for the wire transfer."}
                  </>}
            </p>
            <div className="close-send-link-box"><MessageCircle className="w-5 h-5" /><span>Sending payment details to your WhatsApp...</span></div>
          </div>
        )}
      </div>

      <div className="close-next-steps">
        <h3>What happens next</h3>
        <div className="close-timeline" data-testid="close-timeline">
          <div className="close-tl-step close-tl-active">
            <div className="close-tl-dot" /><div className="close-tl-line" />
            <span className="close-tl-label">Pay now</span>
            <span className="close-tl-time">Takes 2 minutes</span>
          </div>
          <div className="close-tl-step">
            <div className="close-tl-dot" /><div className="close-tl-line" />
            <span className="close-tl-label">Your team is ready</span>
            <span className="close-tl-time">Within 1 hour</span>
          </div>
          <div className="close-tl-step">
            <div className="close-tl-dot" />
            <span className="close-tl-label">Applications live</span>
            <span className="close-tl-time">Day 3</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export { CASE_STUDIES };
